"use client"

import * as React from "react"
import {
  ADMIN_CUSTOM_THEME,
  ADMIN_UI_CHANGE_EVENT,
  DEFAULT_ADMIN_CUSTOM,
  DEFAULT_ADMIN_UI,
  type AdminCustomTheme,
  type AdminThemeChoice,
  type AdminUi,
} from "@/lib/admin/theme/admin-themes"
import { adminCustomFrom, adminUiFrom, customOf, isAdminThemeChoice, sameAdminUi } from "@/lib/admin/theme/admin-ui"
import { readAdminUiCookie } from "@/lib/admin/theme/admin-ui-cookie"
import {
  applyAdminUi,
  clearAdminUi,
  clearCustomerBodyTheme,
  markAdminPreview,
  showAdminTheme,
  withoutTransitions,
} from "@/lib/admin/theme/admin-ui-dom"
import { watchDeviceMode } from "@/lib/admin/theme/admin-mode-store"

export interface AdminUiState {
  /** The admin's saved choice, as the hf-admin-ui cookie mirrors it. */
  ui: AdminUi
  /** The theme on screen: a card the picker is previewing, Custom while a builder is previewed, else the saved one. */
  shownTheme: AdminThemeChoice
  /** The builder on screen: one being previewed (a colour being dragged), else the saved one. */
  shownCustom: AdminCustomTheme
  /** Preview a theme on screen without saving it; null ends the preview. */
  preview: (theme: AdminThemeChoice | null) => void
  /** Preview Custom with these builder settings without saving them; null ends the preview. */
  previewCustom: (custom: AdminCustomTheme | null) => void
}

// Layout effects: <html> must be right before the browser paints. (The server renders nothing here.)
const useIsomorphicLayoutEffect = typeof window === "undefined" ? React.useEffect : React.useLayoutEffect

/**
 * Keeps <html> in step with the admin's console theme while a console layout is mounted (spec
 * docs/superpowers/specs/2026-09-27-admin-console-themes-design.md, "While the console is open").
 *
 *   - On mount it does what the first-paint script does on a full load — theme, builder, Inter
 *     class and light/dark on <html>, the customer app's classes off <body> — because a script
 *     React creates during a client-side move into the console is never executed.
 *   - It follows the choice: the hf-admin-ui-change event (the picker, GET admin/me) means the
 *     cookie changed, so it reads the cookie again — as the shared next-themes wrapper does, so the
 *     two can never disagree. It reads it again when the tab comes back, too: another tab may have
 *     saved a choice, and no event crosses tabs.
 *   - It follows the device while the mode is System.
 *   - It shows previews without saving them: a theme card under the pointer or the keyboard, and a
 *     colour being dragged in the builder's native colour picker (Custom, with that colour).
 *   - On unmount it removes the attribute, the builder and the font class, so nothing follows the
 *     admin out.
 *
 * `initialUi` is what the server read from the cookie, so the first render (the sidebar's style
 * included) matches the server's.
 */
export function useAdminThemeController(initialUi: AdminUi, fontClass?: string): AdminUiState {
  const [ui, setUi] = React.useState<AdminUi>(() => adminUiFrom(initialUi))
  const [previewed, setPreviewed] = React.useState<AdminThemeChoice | null>(null)
  const [previewedCustom, setPreviewedCustom] = React.useState<AdminCustomTheme | null>(null)

  const readCookie = React.useCallback(() => {
    const next = readAdminUiCookie()
    setUi((current) => (sameAdminUi(current, next) ? current : next))
  }, [])

  // Mount: the first-paint script's work, for a client-side arrival.
  useIsomorphicLayoutEffect(() => {
    clearCustomerBodyTheme()
    readCookie()
  }, [readCookie])

  // <html> follows the choice (or what is being previewed) — and the device, while the mode is System.
  // Every swap runs with transitions off for a frame, and a preview marks <html> with the saved
  // theme, so the root keeps its size and the menu its face while the pointer is on a card.
  useIsomorphicLayoutEffect(() => {
    const shown: AdminUi = previewedCustom ? { ...ui, theme: ADMIN_CUSTOM_THEME, custom: previewedCustom } : ui
    const previewing = !!previewedCustom || (previewed !== null && previewed !== ui.theme)
    const apply = () =>
      withoutTransitions(() => {
        applyAdminUi(shown, fontClass)
        if (previewed) showAdminTheme(previewed)
        markAdminPreview(previewing ? ui.theme : null)
      })
    apply()
    return ui.mode === "system" ? watchDeviceMode(apply) : undefined
  }, [ui, previewed, previewedCustom, fontClass])

  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") readCookie()
    }
    window.addEventListener(ADMIN_UI_CHANGE_EVENT, readCookie)
    window.addEventListener("focus", readCookie)
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      window.removeEventListener(ADMIN_UI_CHANGE_EVENT, readCookie)
      window.removeEventListener("focus", readCookie)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [readCookie])

  // Leaving the console: nothing follows the admin out. (Declared last, so it runs after the rest.)
  useIsomorphicLayoutEffect(() => () => clearAdminUi(fontClass), [fontClass])

  const preview = React.useCallback((theme: AdminThemeChoice | null) => {
    setPreviewed(isAdminThemeChoice(theme) ? theme : null)
  }, [])

  const previewCustom = React.useCallback((custom: AdminCustomTheme | null) => {
    setPreviewedCustom(custom ? adminCustomFrom(custom) : null)
  }, [])

  return React.useMemo(
    () => ({
      ui,
      shownTheme: previewed ?? (previewedCustom ? ADMIN_CUSTOM_THEME : ui.theme),
      shownCustom: previewedCustom ?? customOf(ui),
      preview,
      previewCustom,
    }),
    [ui, previewed, previewedCustom, preview, previewCustom],
  )
}

const AdminUiContext = React.createContext<AdminUiState | null>(null)

const OUTSIDE_THE_CONSOLE: AdminUiState = Object.freeze({
  ui: Object.freeze({ ...DEFAULT_ADMIN_UI }),
  shownTheme: DEFAULT_ADMIN_UI.theme,
  shownCustom: DEFAULT_ADMIN_CUSTOM,
  preview: () => {},
  previewCustom: () => {},
})

/** Mounted by both console layouts, seeded with what the server read from the cookie. */
export function AdminUiProvider({
  initialUi,
  fontClass,
  children,
}: {
  initialUi: AdminUi
  fontClass?: string
  children: React.ReactNode
}) {
  const state = useAdminThemeController(initialUi, fontClass)
  return <AdminUiContext.Provider value={state}>{children}</AdminUiContext.Provider>
}

/** The admin's console theme. Outside a console layout: classic / system, and no preview. */
export function useAdminUi(): AdminUiState {
  return React.useContext(AdminUiContext) ?? OUTSIDE_THE_CONSOLE
}

/** Inside the super-admin console (either console layout), where no tenant is on screen. */
export function useInAdminConsole(): boolean {
  return React.useContext(AdminUiContext) !== null
}
